import { create } from "zustand";
import debounce from "lodash.debounce";
import {
  fetchExclusions as apiFetchExclusions,
  upsertExclusion as apiUpsertExclusion,
  deleteExclusion as apiDeleteExclusion,
} from "../services/exclusionsService";

// Глобальний стор (state manager) сторінки "Виключення"
export const useExclusionsStore = create((set, get) => ({
  exclusions: [],
  isLoading: true,
  filters: { searchTerm: "" },

  // Debounced-функція для відкладеного оновлення даних з сервера
  debouncedRefresh: debounce(() => {
    get().fetchExclusions();
  }, 10000),

  // Ініціалізує стор при першому завантаженні сторінки
  init: async () => {
    get().initFiltersFromURL();
    await get().fetchExclusions();
  },

  // Зчитує параметри з URL і встановлює початковий стан фільтрів
  initFiltersFromURL: () => {
    const params = new URLSearchParams(window.location.search);
    set({
      filters: {
        ...get().filters,
        search: params.get("search") || "",
      },
    });
  },

  // Оновлює URL-рядок браузера відповідно до поточного стану фільтрів
  syncFiltersToURL: () => {
    const { filters } = get();
    const params = new URLSearchParams();

    // Додаємо в URL лише непусті значення для чистоти
    if (filters.search) params.set("search", filters.search);

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, "", newUrl);
  },

  // Керує логікою пошуку
  setSearch: (value) => {
    set((state) => ({
      filters: { ...state.filters, search: value },
    }));
    get().syncFiltersToURL();
    get().fetchExclusions();
  },

  // Завантаження всіх виключень
  fetchExclusions: async () => {
    get().debouncedRefresh.cancel();

    set({ isLoading: true });
    try {
      const data = await apiFetchExclusions(get().filters);
      set({ exclusions: data, isLoading: false });
    } catch (err) {
      // У випадку помилки скидаємо дані до порожніх значень
      console.error("Failed to fetch exclusions:", err);
      set({ exclusions: [] });
    } finally {
      set({ isLoading: false });
    }
  },

  // Оптимістично додає або оновлює виключення
  upsertExclusion: async (article, newPrice) => {
    const priceAsNumber = parseFloat(newPrice);
    if (isNaN(priceAsNumber)) {
      console.error("Invalid price provided:", newPrice);
      return; // Не оновлювати стан, якщо ціна некоректна
    }

    const originalExclusions = get().exclusions;
    const existing = originalExclusions.find((ex) => ex.article === article);

    // Готуємо новий масив для миттєвого відображення в UI
    let optimisticExclusions;
    if (existing) {
      // Якщо існує - оновлюємо
      optimisticExclusions = originalExclusions.map((ex) =>
        ex.article === article ? { ...ex, new_price: priceAsNumber } : ex
      );
    } else {
      // Якщо не існує - додаємо в кінець
      optimisticExclusions = [
        ...originalExclusions,
        { article, new_price: priceAsNumber },
      ];
    }

    set({ exclusions: optimisticExclusions }); // Миттєво оновлюємо UI

    try {
      // У фоновому режимі надсилаємо запит на сервер
      await apiUpsertExclusion(article, priceAsNumber);
      get().debouncedRefresh(); // Успіх: плануємо фінальну синхронізацію
    } catch (err) {
      // Помилка: відкочуємо зміни, повертаючи початковий стан
      console.error("Failed to upsert exclusion, rolling back:", err);
      set({ exclusions: originalExclusions });
    }
  },

  // Оптимістично видаляє виключення
  deleteExclusion: async (article) => {
    const originalExclusions = get().exclusions;

    // Готуємо новий масив без видаленого елемента
    const optimisticExclusions = originalExclusions.filter(
      (ex) => ex.article !== article
    );

    set({ exclusions: optimisticExclusions }); // Миттєве оновлення UI

    try {
      // У фоновому режимі надсилаємо запит на сервер
      await apiDeleteExclusion(article);
      get().debouncedRefresh(); // Успіх: плануємо фінальну синхронізацію
    } catch (err) {
      // Помилка: відкочуємо зміни, повертаючи початковий стан
      console.error("Failed to delete exclusion, rolling back:", err);
      set({ exclusions: originalExclusions });
    }
  },
}));
